<template>
    <div id="client-form-container">

        <div v-if="loading">
            <div class="spinner"></div>
        </div>

        <div v-else-if="errors.length" class="alert--error">
            <p v-for="err in errors" :key="err"> {{ err }} </p>
        </div>

        <form v-if="!loading" class="form" @submit.prevent="save">
            <slot name="header"></slot>

            <div :class="wrapperClass">
                <h3 class="first">{{$t('forms.client.personal_info')}}</h3>

                <div class="row">
                    <div class="col-12 col-md-4">
                        <div class="form__item">
                            <i class="form__icon icons icon-user"></i>
                            <input v-model="client.initials" class="form__input--text" type="text" :class="{ 'has-value': client.initials }"/>
                            <label class="form__label">{{$t('forms.client.initials')}}</label>
                        </div>
                    </div>
                    <div class="col-12 col-md-8">
                        <div class="form__item">
                            <input v-model="client.first_name" class="form__input--text" type="text" :class="{ 'has-value': client.first_name }"/>
                            <label class="form__label">{{$t('forms.client.first_name')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 col-md-12 col-lg-12">
                        <div class="form__item">
                            <input v-model="client.maiden_name" class="form__input--text" type="text" :class="{ 'has-value': client.maiden_name }"/>
                            <label class="form__label">{{$t('forms.client.maiden_name')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 col-md-6 col-lg-12">
                        <div class="form__item">
                            <input v-model="client.last_name" class="form__input--text" type="text" :class="{ 'has-value': client.last_name }"/>
                            <label class="form__label">{{$t('forms.client.last_name')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <label class="form__top_label">{{$t('forms.client.gender')}}</label>
                            <div class="radio is-inline">
                                <input type="radio" v-model="client.gender" class="radio__rb" id="gender_m" value="m">
                                <label class="radio__label" for="gender_m">{{$t('forms.common.man')}}</label>
                            </div>
                            <div class="radio is-inline">
                                <input type="radio" v-model="client.gender" class="radio__rb" id="gender_w" value="w">
                                <label class="radio__label" for="gender_w">{{$t('forms.common.woman')}}</label>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-calendar"></i>
                            <input v-model="client.date_of_birth" class="form__input--text has-value" type="date"/>
                            <label class="form__label">{{$t('forms.client.date_of_birth')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-globe"></i>
                            <input v-model="client.nationality" class="form__input--text" type="text" :class="{ 'has-value': client.nationality }"/>
                            <label class="form__label">{{$t('forms.client.nationality')}}</label>
                        </div>
                    </div>
                </div>

                <h3>{{$t('forms.client.address_info')}}</h3>

                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-location-pin"></i>
                            <input v-model="client.address" class="form__input--text" type="text" :class="{ 'has-value': client.address }"/>
                            <label class="form__label">{{$t('forms.client.address')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12 col-md-6">
                        <div class="form__item">
                            <input v-model="client.address_number" class="form__input--text" type="text" :class="{ 'has-value': client.address_number }"/>
                            <label class="form__label">{{$t('forms.client.address_number')}}</label>
                        </div>
                    </div>
                    <div class="col-12 col-md-6">
                        <div class="form__item">
                            <input v-model="client.address_number_suffix" class="form__input--text" type="text" :class="{ 'has-value': client.address_number_suffix }"/>
                            <label class="form__label">{{$t('forms.client.address_number_suffix')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <input v-model="client.postal_code" class="form__input--text" type="text" :class="{ 'has-value': client.postal_code }"/>
                            <label class="form__label">{{$t('forms.client.postal_code')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <input v-model="client.city" class="form__input--text" type="text" :class="{ 'has-value': client.city }"/>
                            <label class="form__label">{{$t('forms.client.city')}}</label>
                        </div>
                    </div>
                </div>

                <h3>{{$t('forms.client.contact_info')}}</h3>
                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-phone"></i>
                            <input v-model="client.phone_number" class="form__input--text" type="text" :class="{ 'has-value': client.phone_number }"/>
                            <label class="form__label">{{$t('forms.client.phone_number')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <input v-model="client.mobile_number" class="form__input--text" type="text" :class="{ 'has-value': client.mobile_number }"/>
                            <label class="form__label">{{$t('forms.client.mobile_number')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-envelope"></i>
                            <input v-model="client.email" class="form__input--text" type="email" :class="{ 'has-value': client.email }"/>
                            <label class="form__label">{{$t('forms.client.email')}}</label>
                        </div>
                    </div>
                </div>

                <h3>{{$t('forms.client.education_work_info')}}</h3>
                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <input v-model="client.benefits" class="form__input--text" type="text" :class="{ 'has-value': client.benefits }"/>
                            <label class="form__label">{{$t('forms.client.benefits')}}</label>
                        </div>
                    </div>
                </div>


                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <i class="form__icon icons icon-graduation"></i>
                            <input v-model="client.education" class="form__input--text" type="text" :class="{ 'has-value': client.education }"/>
                            <label class="form__label">{{$t('forms.client.education')}}</label>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <div class="form__item">
                            <input v-model="client.intellectual_ability" class="form__input--text" type="text" :class="{ 'has-value': client.intellectual_ability }"/>
                            <label class="form__label">{{$t('forms.client.intellectual_ability')}}</label>
                        </div>
                    </div>
                </div>
            </div>

            <slot name="buttons" :isNextDisabled="isNextDisabled">
                <div class="form__buttons">
                    <button type="submit" class="form__button button--cta" :class="{disabled: isNextDisabled}" :disabled="isNextDisabled">{{$t('common.save')}}</button>
                </div>
            </slot>
        </form>
    </div>
</template>

<script>
    import Vue from 'vue';
    import { Component, Prop, Watch } from 'vue-property-decorator';
    import Client from '@/models/Client';

    @Component
    export default class ClientForm extends Vue {

        @Prop({ type: Client, default() {
            return new Client();
        } })
        client;

        @Prop({ type: Array | String, default() {
            return [];
        } })
        wrapperClass;

        errors = [];
        loading = false;

        created() {
            this.errors = [];
        }

        isNextDisabled = false;

        @Watch('client', { immediate: true, deep: true })
        onClientFormChange(client) {
            this.isNextDisabled = !client.email || !client.first_name || !client.last_name;
        }

        save() {
            this.errors = [];
            this.loading = true;
            this.client.save()
                .then(res => {
                    this.$emit('saved', res);
                    this.$notify.success(this.$t('notify.client.save_success'));
                })
                .catch(
                    err => {
                        if (err.status === 422) {
                            this.errors = err.errors;
                            this.$notify.error(this.$t('common.validation_error'));
                        } else {
                            this.errors = [this.$t('common.unknown_error')];
                        }
                    }
                )
                .then(() => {
                    this.loading = false;
                    this.$scrollTo('#client-form-container');
                });
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/forms/dashboard/ClientForm.vue